/**
 * The basket about to be placed, as payoff legs — ROADMAP 5.22.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE CURVE IS DRAWN AT THE PRICES THE ORDER WILL ACTUALLY PAY
 *
 * Not the last traded price. A sold leg is filled on the BID and a bought
 * leg on the ASK, which is the same rule `netPremiumOf` prices the basket
 * with — so the chart, the net premium beside it and the order that follows
 * all describe one trade. Drawing the LTP curve instead would put the
 * breakevens in a slightly better place than the trade can reach, every
 * time, on the screen used to decide whether to take it.
 *
 * NO PRICE, NO CURVE.
 *
 * When a leg has neither a touch nor a last trade, this returns null and the
 * caller says so. A payoff drawn through a missing price is not a cautious
 * estimate — it is a specific, wrong claim about where the position breaks
 * even, and §4.8a's rule applies here too: a thing we do not know must not
 * be rendered as a thing we do.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import type { PayoffLeg, ResolvedLeg } from '@apna/shared'

/** The price a leg would cross at, or null when the book cannot say. */
function entryPriceOf(leg: ResolvedLeg): number | null {
  const touch = leg.side === 'SELL' ? leg.bid : leg.ask
  if (typeof touch === 'number' && touch > 0) return touch
  /*
   * The last trade, when the touch is missing. A quiet far strike often has
   * no quote at all between trades, and refusing to draw the whole basket
   * because one wing is untouched would hide the shape at exactly the moment
   * it is being chosen. It is a worse price than the touch, never a invented
   * one, and the net premium beside the chart is computed the strict way.
   */
  return leg.ltp > 0 ? leg.ltp : null
}

/**
 * Signed, lot-expanded legs for `PayoffChart`, or null if any is unpriced.
 *
 * `lots` on a resolved leg already carries the template's ratio, so the only
 * multiplication left is the lot size.
 */
export function payoffLegsOf(
  legs: readonly ResolvedLeg[],
  lotSize: number,
): PayoffLeg[] | null {
  if (legs.length === 0 || !(lotSize > 0)) return null

  const out: PayoffLeg[] = []
  for (const leg of legs) {
    const avgPrice = entryPriceOf(leg)
    if (avgPrice === null) return null
    out.push({
      netQty: (leg.side === 'BUY' ? 1 : -1) * leg.lots * lotSize,
      avgPrice,
      optionType: leg.optionType,
      strike: leg.strike,
    })
  }
  return out
}
